/**
 * Not on this computer yet (box hosts, Plan 2c).
 *
 * A feature that cannot reach the user's computer says so in these words, with this code. It is a
 * state, not a fault: never a 500, never "try again". The box door (spec §6, Plan 3b) replaced most of
 * its uses: files, the library, attachments, Nami, the terminal, the logs, the admin file tools and the
 * thumbnails reach every cloud host now. What still answers it:
 *
 * - The previews: `{ ok: false, error: 'not_on_this_computer' }` (types/live-preview.ts) for a box they
 *   cannot reach (the person's own computer; a boat app goes through the door).
 * - The server's routes that stay on Blaxel (ruling 3b-R9: the debug cleanup and kill-processes, the
 *   admin force upgrade): HTTP 409 with NOT_ON_THIS_COMPUTER_BODY (apps/server hosts/not-on-this-computer.ts).
 * - The web shows the words (notOnThisComputerMessage), and keeps reading the code: an older runner or a
 *   page loaded before the box door can still carry it.
 */

/** The code. Not `sandbox_unavailable`, which the web reads as "try again in a moment". */
export const NOT_ON_THIS_COMPUTER_CODE = 'not_on_this_computer' as const;

/** The words, shown as they are. */
export const NOT_ON_THIS_COMPUTER_YET = 'Not available on this computer yet.';

export interface NotOnThisComputerBody {
  error: typeof NOT_ON_THIS_COMPUTER_CODE;
  message: string;
}

/** The HTTP body, the same on every route. */
export const NOT_ON_THIS_COMPUTER_BODY: Readonly<NotOnThisComputerBody> = Object.freeze({
  error: NOT_ON_THIS_COMPUTER_CODE,
  message: NOT_ON_THIS_COMPUTER_YET,
});

/** The words to show for a not-on-this-computer answer, or null for any other body. */
export function notOnThisComputerMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const { error, message } = body as { error?: unknown; message?: unknown };
  if (error !== NOT_ON_THIS_COMPUTER_CODE) return null;
  return typeof message === 'string' && message.trim() !== '' ? message : NOT_ON_THIS_COMPUTER_YET;
}
